import {STEP_STATE} from '@angular/cdk/stepper';
import {HttpErrorResponse} from '@angular/common/http';
import {ChangeDetectorRef, Component, effect, signal, Signal, viewChild} from '@angular/core';
import {ReactiveFormsModule, UntypedFormGroup} from '@angular/forms';
import {MatButtonModule} from '@angular/material/button';
import {MatIconModule} from '@angular/material/icon';
import {MatRadioModule} from '@angular/material/radio';
import {MatSnackBar} from '@angular/material/snack-bar';
import {MatStepper, MatStepperModule} from '@angular/material/stepper';
import {ActivatedRoute, Router} from '@angular/router';
import {AuthDataService} from '@authentication/authentication-data.service';
import {DatasetCreate, DatasetResponse, LabelResponse, DatasetsService as LSDatasetsService} from '@backend';
import {ValidationErrorHandler} from '@forms/validationerrorhandler.service';
import {IndexService} from '@middleware';
import {CommonsSearchDataset} from '@models/commons-types';
import {FormlyFieldConfig, FormlyFormOptions, FormlyModule} from '@ngx-formly/core';
import {AttestationService} from '@services/attestation/attestation.service';
import {LandingServiceBrokerService} from '@services/landing-service-broker/landing-service-broker.service';
import {SearchService} from '@services/search/search.service';
import {SectionHeaderActions} from '@shared/components/section-header-actions/section-header-actions';
import {REDCAP_TOKEN_PLACEHOLDER} from '@shared/constants/constants';
import {DatasetAggregationLevel} from '@shared/labels/dataset-aggregation-level';
import {IdentifierHipaa, IdentifierHipaaOptions} from '@shared/labels/identifier-hipaa';
import {SensitivityLevel} from '@shared/labels/sensitivity-level';
import {UUID} from 'crypto';
import {concatMap, from, lastValueFrom, zip} from 'rxjs';
import {buildDatasetDetailsFields} from '../dataset.form.config';

export const DATASET_PII_ATTESTATION =
   'I confirm that the text entered here in Dataset Details ' +
  'does not contain sensitive data (such as Personally Identifiable Information).';
export const DATASET_PII_ATTESTATION_HSD =
  'I confirm that the text entered here in Dataset Details ' +
  'does not contain sensitive data (such as Personally Identifiable Information).' +
  ' I understand this type of data should be limited to the associated data files I upload.';

function isHSD(model: DatasetCreate & {__redcap_extract_options: string; __has_hipaa?: boolean}) {
  return (
    model.__has_hipaa &&
    model.identifiers_hipaa.some(id => IdentifierHipaaOptions[id].sensitivity_level_id === SensitivityLevel.HSD)
  );
}

abstract class DatasetAddEditBaseComponent {
  public model: DatasetCreate & {__redcap_extract_options: string; __has_hipaa?: boolean} = <typeof this.model>{};
  public overviewFormOptions: FormlyFormOptions = {};
  public dataContentsFormOptions: FormlyFormOptions = {};
  public regulatoryFormOptions: FormlyFormOptions = {};
  public overviewFormFields: FormlyFieldConfig[] = [];
  public dataContentsFormFields: FormlyFieldConfig[] = [];
  public regulatoryFormFields: FormlyFieldConfig[] = [];
  public overviewFormGroup = new UntypedFormGroup({});
  public dataContentsFormGroup = new UntypedFormGroup({});
  public regulatoryFormGroup = new UntypedFormGroup({});
  public form = new UntypedFormGroup({
    Overview: this.overviewFormGroup,
    'Data Contents': this.dataContentsFormGroup,
    Regulatory: this.regulatoryFormGroup,
  });
  public lsDatasetService: LSDatasetsService;
  public storageOrgName: string;
  public submitting = false;
  public datasetId: string;

  constructor(
    public readonly isCreating: boolean,
    projectId: string,
    lsbs: LandingServiceBrokerService,
    searchService: SearchService,
    authDataService: AuthDataService,
    public readonly snackbarService: MatSnackBar,
    private readonly router: Router,
    private readonly indexService: IndexService,
    changeDetector: ChangeDetectorRef,
    private readonly attestationService: AttestationService,
    private readonly validationErrorHandler: ValidationErrorHandler,
  ) {
    const obsPubProject = from(
      searchService.searchByID({id: {terms: projectId}}, {public_view: false, page: 1, per_page: 1}, 'Projects'),
    );
    const obsBackend = obsPubProject.pipe(
      concatMap(pubProject => lsbs.getByOrganizationName(pubProject.storage_organization.name)),
    );
    const obsPrvProject = obsBackend.pipe(concatMap(services => services.ProjectsService.getProject(projectId)));
    zip(obsPubProject, obsBackend, obsPrvProject).subscribe(([pubProject, services, prvProject]) => {
      this.lsDatasetService = services.DatasetsService;
      this.storageOrgName = pubProject.storage_organization.name;
      if (this.isCreating)
        Object.assign(this.model, {
          associated_projects: [
            {
              project_id: pubProject.id,
              is_primary_project: true,
            },
          ],
          users: [
            {
              email: authDataService.me().email,
              role: 'admin',
            },
          ],
          is_data_public: false,
          visible_to_all_users: false,
          organizational_access: false,
          show_storage_organization_logo: true,
          bids_structure: false,
          quality: 'unknown',
        });
      const fieldData = buildDatasetDetailsFields(prvProject, services);
      [this.overviewFormFields, this.dataContentsFormFields, this.regulatoryFormFields] = fieldData.fields;
      changeDetector.detectChanges();
    });
  }

  public handleBrowserSubmit($event: SubmitEvent) {
    $event.preventDefault();
    return false;
  }

  async submit(event: Event): Promise<void> {
    event.preventDefault();
    if (this.form.pending) {
      this.snackbarService.open("We're making sure everything's alright, try again in a few seconds.", undefined, {
        duration: 5000,
      });
      return;
    } else if (!this.form.valid) {
      this.validationErrorHandler.handleError([
        ...this.overviewFormFields,
        ...this.dataContentsFormFields,
        ...this.regulatoryFormFields,
      ]);
      return;
    }
    if (
      !(await this.attestationService.confirmWithCancel(
        isHSD(this.model) ? DATASET_PII_ATTESTATION_HSD : DATASET_PII_ATTESTATION,
      ))
    )
      return;
    try {
      this.submitting = true;
      this.form.markAsPristine();
      await this.submitImpl();
    } catch (e) {
      this.validationErrorHandler.handleError(
        [...this.overviewFormFields, ...this.dataContentsFormFields, ...this.regulatoryFormFields],
        <HttpErrorResponse>e,
      );
      return;
    } finally {
      this.submitting = false;
    }
    if (this.datasetId != undefined) {
      try {
        this.submitting = true;

        await lastValueFrom(
          this.indexService.syncDatasetInIndex(this.datasetId, {storage_organization: this.storageOrgName}),
        );
      } catch (e) {
        console.error('Dataset sync failed', {
          datasetId: this.datasetId,
          storageOrganization: this.storageOrgName,
          error: e,
        });

        this.snackbarService.open(
          `Your dataset has been ${this.isCreating ? 'created' : 'saved'} in the Private Data Enclave, but isn't yet available. Please contact the RDE support team.`,
          undefined,
          {duration: 15000, panelClass: 'snackbar-error'},
        );
        this.submitting = false;
        return;
      }

      await this.router.navigate(['/private-data-enclave/dataset/view', this.datasetId]);
    }
  }

  convertModel(model: typeof this.model): DatasetCreate {
    const data = {...model};
    if (model.__has_hipaa === false) {
      data.identifiers_hipaa = [IdentifierHipaa.NA];
      data.data_aggregation_id = DatasetAggregationLevel.NONE;
      data.study_irb_number = '';
      data.dsp_id = '';
    }

    delete data.__has_hipaa;

    if (data.dataset_type === 'REDCap') {
      const mode = model.__redcap_extract_options;

      if (mode !== 'redcap_report') delete data.redcap_report_id;

      data.is_data_dictionary = mode === 'data_dictionary';
      data.redcap_extract_data = mode !== 'none';

      delete data.__redcap_extract_options;
    }

    if (data.dataset_type !== 'DICOM') {
      //@ts-expect-error Forcing type conformity
      delete data.bids_structure;
      //@ts-expect-error Forcing type conformity
      delete data.quality;
    }

    return data;
  }

  /**
   * {@link submit} handles a bunch of setup work -- preparing the payload, handling
   * client-side validation, etc -- and resolution work -- handling server-side
   * validation, redirecting to the new or updated dataset, etc. However, different
   * forms need different endpoints to actually submit. This function performs
   * the actual submission, allowing varying those endpoints.
   */
  abstract submitImpl(): Promise<void>;

  handleBackFromStep2($event: any): void {
    window.history.go(-1);
  }

  abstract stepper: Signal<MatStepper>;

  validateAndNext(): void {
    const stepIndex = this.stepper().selectedIndex;
    const formGroups = [null, this.overviewFormGroup, this.dataContentsFormGroup, this.regulatoryFormGroup];
    const currentGroup = formGroups[stepIndex];
    if (currentGroup) {
      currentGroup.markAllAsTouched();
      if (!currentGroup.valid) return;
    }
    this.stepper().next();
  }
}

@Component({
  selector: 'app-dataset-add',
  imports: [MatStepperModule, MatRadioModule, MatIconModule, FormlyModule, ReactiveFormsModule, MatButtonModule],
  templateUrl: './dataset-add-edit.html',
  styleUrl: './dataset-add-edit.scss',
})
export class DatasetAddComponent extends DatasetAddEditBaseComponent implements SectionHeaderActions {
  override stepper: Signal<MatStepper> = viewChild.required(MatStepper);

  constructor(
    route: ActivatedRoute,
    lsbs: LandingServiceBrokerService,
    searchService: SearchService,
    authDataService: AuthDataService,
    snackbarService: MatSnackBar,
    router: Router,
    indexService: IndexService,
    changeDetector: ChangeDetectorRef,
    attestationService: AttestationService,
    validationErrorHandler: ValidationErrorHandler,
  ) {
    const projectId = route.snapshot.params['project'];
    super(
      true,
      projectId,
      lsbs,
      searchService,
      authDataService,
      snackbarService,
      router,
      indexService,
      changeDetector,
      attestationService,
      validationErrorHandler,
    );
  }

  public sectionHeaderActions = signal([
    {
      target: '/help/private-data-enclave/add-private-dataset',
      targetTab: '_blank',
      caption: 'Help',
      color: 'var(--mat-sys-primary)',
      icon: 'help_outline',
    },
  ]);

  override async submitImpl(): Promise<void> {
    const response = await lastValueFrom(
      this.lsDatasetService.createDataset(this.convertModel(this.model), {observe: 'response'}),
    );
    if (!response.ok)
      throw new HttpErrorResponse({
        status: response.status,
        url: response.url,
        headers: response.headers,
        error: response.body,
      });
    const dataset_hateoas = response.headers.get('Location');
    this.datasetId = <UUID>dataset_hateoas.substring(dataset_hateoas.lastIndexOf('/') + 1);
  }
}

@Component({
  selector: 'app-dataset-edit',
  imports: [MatStepperModule, MatRadioModule, MatIconModule, FormlyModule, ReactiveFormsModule, MatButtonModule],
  templateUrl: './dataset-add-edit.html',
  styleUrl: './dataset-add-edit.scss',
})
export class DatasetEditComponent extends DatasetAddEditBaseComponent implements SectionHeaderActions {
  override stepper: Signal<MatStepper> = viewChild.required(MatStepper);

  constructor(
    route: ActivatedRoute,
    lsbs: LandingServiceBrokerService,
    searchService: SearchService,
    authDataService: AuthDataService,
    snackbarService: MatSnackBar,
    router: Router,
    indexService: IndexService,
    changeDetector: ChangeDetectorRef,
    attestationService: AttestationService,
    validationErrorHandler: ValidationErrorHandler,
  ) {
    const dataset: DatasetResponse & CommonsSearchDataset = route.snapshot.data['dataset'];
    const projectLink = dataset.associated_projects.find(project => project.is_primary_project);
    const projectId = projectLink.project.id;
    super(
      false,
      projectId,
      lsbs,
      searchService,
      authDataService,
      snackbarService,
      router,
      indexService,
      changeDetector,
      attestationService,
      validationErrorHandler,
    );
    this.datasetId = dataset.id;
    Object.assign(this.model, {
      __has_hipaa: dataset.identifiers_hipaa.some(
        label => (<LabelResponse>IdentifierHipaaOptions[label.id]).sensitivity_level_id != SensitivityLevel.NA,
      ),
      attribution: dataset.attribution,
      contract_id: projectLink.contract?.id,
      data_aggregation_id: dataset.data_aggregation?.id,
      dataset_type: dataset.dataset_type,
      description: dataset.description,
      doi: dataset.doi,
      dsp_id: projectLink.data_security_plan?.id,
      identifiers_hipaa: dataset.identifiers_hipaa.map(label => label.id),
      keywords: dataset.keywords,
      license_id: dataset.license?.id,
      link_to_external_dataset: dataset.link_to_external_dataset,
      name: dataset.name,
      original_datasets: dataset.original_datasets.map(ds => ds.id),
      other_sensitive_data: dataset.other_sensitive_data.map(label => label.id),
      show_storage_organization_logo: dataset.show_storage_organization_logo,
      study_irb_number: dataset.study_irb_number,
      variables_measured: dataset.variables_measured,
      organizational_access: dataset.organizational_access,
      visible_to_all_users: dataset.visible_to_all_users,
      is_data_public: dataset.is_data_public,
      users: dataset.dataset_users.map(user => ({email: user.user.email, role: user.dataset_role_id})),
      associated_projects: dataset.associated_projects.map(project => ({
        project_id: project.project.id,
        is_primary_project: project.is_primary_project,
      })),
    });
    if (dataset.dataset_type == 'DICOM') {
      Object.assign(this.model, {
        bids_structure: dataset.dicom_metadata.bids_structure,
        quality: dataset.dicom_metadata.quality,
      });
    }
    if (dataset.dataset_type == 'REDCap') {
      Object.assign(this.model, {
        redcap_extract_data: dataset.redcap_metadata.redcap_extract_data,
        redcap_project_pi: dataset.redcap_metadata.redcap_project_pi,
        redcap_project_title: dataset.redcap_metadata.redcap_project_title,
        redcap_project_url: dataset.redcap_metadata.redcap_project_url,
        redcap_report_id: dataset.redcap_metadata.redcap_report_id,
        is_data_dictionary: dataset.redcap_metadata.is_data_dictionary,
        redcap_project_token: REDCAP_TOKEN_PLACEHOLDER,
      });
      if (!dataset.redcap_metadata.redcap_extract_data) {
        this.model.__redcap_extract_options = 'none';
      } else if (dataset.redcap_metadata.is_data_dictionary) {
        this.model.__redcap_extract_options = 'data_dictionary';
      } else if (dataset.redcap_metadata.redcap_report_id) {
        this.model.__redcap_extract_options = 'redcap_report';
      } else {
        this.model.__redcap_extract_options = 'extract_all';
      }
    }
    effect(() => {
      if (this.stepper()) {
        this.stepper().selectedIndex = 1;
        this.stepper().steps.forEach(step => {
          step.interacted = true;
          step.completed = true;
          step.state = step.index() == 0 ? STEP_STATE.DONE : STEP_STATE.EDIT;
        });
      }
    });
  }

  public sectionHeaderActions = signal([
    {
      target: '/help/private-data-enclave/add-private-dataset',
      targetTab: '_blank',
      caption: 'Help',
      color: 'var(--mat-sys-primary)',
      icon: 'help_outline',
    },
  ]);

  override async submitImpl(): Promise<void> {
    await lastValueFrom(this.lsDatasetService.updateDataset(this.datasetId, this.convertModel(this.model)));
  }
}
